import { useCallback, useEffect, useRef, useState } from 'react';

const SHIELD_ATTR = 'data-shot-shield';
const MAC_SHOT_CODES = ['Digit3', 'Digit4', 'Digit5', 'Digit6'];
const LOCK_KEYS = ['MetaLeft', 'MetaRight', 'PrintScreen'];
const isMac = /Mac|iPhone|iPad/i.test(navigator.userAgentData?.platform || navigator.platform || navigator.userAgent);

const setShield = (on) => (on ? document.documentElement.setAttribute(SHIELD_ATTR, '') : document.documentElement.removeAttribute(SHIELD_ATTR));
const wipeClipboard = () => { try { navigator.clipboard?.writeText?.(' ')?.catch(() => {}); } catch { /* not allowed */ } };
const isKey = (e, name) => (e.key || '').toLowerCase() === name;
const isMeta = (e) => isKey(e, 'meta') || isKey(e, 'os');
const isPrintScreen = (e) => isKey(e, 'printscreen') || e.code === 'PrintScreen' || e.keyCode === 44;

// Blurs the screen the instant a screenshot key/shortcut starts (and while Win/Cmd is held), wipes a copied shot, reports it once
export const useScreenshotGuard = ({ enabled, onCaught }) => {
    const [caught, setCaught] = useState(false);
    const doneRef = useRef(false);
    const onCaughtRef = useRef(onCaught);
    onCaughtRef.current = onCaught;

    const reset = useCallback(() => { doneRef.current = false; setCaught(false); setShield(false); }, []);

    useEffect(() => {
        if (!enabled) return undefined;
        const s = { meta: false, shift: false, other: false };
        const catchShot = (kind, key) => {
            setShield(true);
            wipeClipboard();
            if (doneRef.current) return;
            doneRef.current = true;
            setCaught(true);
            onCaughtRef.current(kind, key);
        };
        const release = () => {
            s.meta = false; s.shift = false; s.other = false;
            if (!doneRef.current && document.hasFocus()) setShield(false);
        };

        const onKeyDown = (e) => {
            if (isPrintScreen(e)) { e.preventDefault(); catchShot('printscreen', 'PrintScreen'); return; }
            if (isMeta(e)) { s.meta = true; s.shift = e.shiftKey; s.other = false; setShield(true); return; }
            if (isKey(e, 'shift')) { if (s.meta) s.shift = true; return; }
            if (e.shiftKey && (e.metaKey || e.ctrlKey) && e.code === 'KeyS') {
                e.preventDefault(); e.stopPropagation();
                catchShot(!isMac && e.metaKey ? 'snipping_tool' : 'browser_screenshot', e.metaKey ? (isMac ? 'Cmd+Shift+S' : 'Win+Shift+S') : 'Ctrl+Shift+S');
                return;
            }
            if (e.shiftKey && e.metaKey && MAC_SHOT_CODES.includes(e.code)) {
                e.preventDefault(); e.stopPropagation();
                catchShot('mac_screenshot', `Cmd+Shift+${e.code.slice(-1)}`);
                return;
            }
            if (s.meta && !e.metaKey) release();
            else if (s.meta) s.other = true;
        };
        const onKeyUp = (e) => {
            if (isPrintScreen(e)) { catchShot('printscreen', 'PrintScreen'); return; }
            if (!isMeta(e)) return;
            // Mac: Cmd+Shift+3/4/5 never reach the page; Cmd+Shift let go with no key in between while still focused = a system screenshot
            if (isMac && s.shift && !s.other && document.hasFocus()) { catchShot('mac_screenshot', 'Cmd+Shift'); return; }
            release();
        };
        const onBlur = () => {
            // Win+Shift+S / Cmd+Shift+5 open a capture overlay that takes focus without sending the last key to the page
            if (s.meta && s.shift && !s.other) { catchShot(isMac ? 'mac_screenshot' : 'snipping_tool', isMac ? 'Cmd+Shift' : 'Win+Shift+S'); return; }
            s.meta = false; s.shift = false; s.other = false;
            setShield(true);
        };
        const onFocus = () => release();
        const onPointer = (e) => { if (s.meta && !e.metaKey) release(); };
        const onPointerDown = () => { if (s.meta) s.other = true; };
        const onFullscreen = () => {
            // In app full screen the browser can keep Win/Cmd and PrtSc away from the OS, so they reach the checks above
            if (document.fullscreenElement && navigator.keyboard?.lock) navigator.keyboard.lock(LOCK_KEYS).catch(() => {});
        };

        window.addEventListener('keydown', onKeyDown, true);
        window.addEventListener('keyup', onKeyUp, true);
        window.addEventListener('blur', onBlur);
        window.addEventListener('focus', onFocus);
        document.addEventListener('pointermove', onPointer, true);
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('wheel', onPointerDown, true);
        document.addEventListener('fullscreenchange', onFullscreen);
        onFullscreen();
        return () => {
            window.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('keyup', onKeyUp, true);
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('pointermove', onPointer, true);
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('wheel', onPointerDown, true);
            document.removeEventListener('fullscreenchange', onFullscreen);
            if (!doneRef.current) setShield(false);
        };
    }, [enabled]);

    return { caught, reset };
};
